package mapsearch

import apiclient.FormationClient
import apiclient.geoobjects.MarkerColor
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.SearchSpec
import apiclient.mapsearch.getTiles
import apiclient.util.JsSimpleCache
import com.jillesvangurp.geo.tiles.Tile
import kotlin.math.min
import kotlin.time.Duration.Companion.seconds
import kotlin.time.measureTimedValue
import model.MapState
import mu.KotlinLogging
import utils.resolveColor

private val logger = KotlinLogging.logger("MapTileCache")

/**
 * The MapTileCache is only valid for a given searchSpec, of course. Make sure to clear it when you change the searchSpec.
 *
 * The key thing is that we want caching for tiles and we don't want to waste a lot of time iterating
 * over tiles and their markers. That's why a lot of the state is private. Keep all the magic contained in this class.
 */
class MapTileCache(val formationClient: FormationClient) {
    companion object {
        const val TILE_CACHE_SIZE = 500
        val TILE_EXPIRATION = 120.seconds
    }

    // tiles are cached as we fetch them from the server to avoid hammering the server with requests for new tiles
    val cache =
        JsSimpleCache<String, MapMarkerTileSearchResultsHolder>(TILE_CACHE_SIZE, TILE_EXPIRATION.inWholeMilliseconds)

    // dirty tiles contain overrides with possibly newer content
    // any overrides will get applied to the tiles to make sure that
    // local changes override older content from the server due to caching issues
    // IMPORTANT: keep the overrides longer than the tile cache entries
//    private val dirtyTiles = JsSimpleCache<String, List<MapMarker>>(
//        // FIXME don't apply overrides to zl 0,1,2
//        // times max zoom levels, just in case
//        capacity = TILE_CACHE_SIZE * 23,
//        // add a little grace period to ensure overrides survive the tile they are in
//        maxAgeInMillis = (TILE_EXPIRATION + 3.seconds).inWholeMilliseconds,
//    )

    /**
     * Clears the cache but keeps the overrides. Call this whenever you change the searchSpec.
     */
    fun clear() {
        cache.clear()
    }

    /**
     * Ensures that the marker is swapped out for the newer replacement in any tile that has it.
     */
//    fun addOverride(obj: GeoObjectDetails) {
//        addOverride(obj.mapMarker())
//    }

    /**
     * Ensures that the marker is swapped out for the newer replacement in any tile that has it.
     */
//    fun addOverride(marker: MapMarker) {
//        // re-fetch any outdated tiles
//        invalidate(marker.position)
//        // make sure we have the new version to override any stale tiles still coming from the server
//
//        marker.position.pointCoordinates().tiles().forEach { tile ->
//            val path = tile.path
//            val existing = dirtyTiles[path]
//            if (existing == null) {
//                dirtyTiles[path] = listOf(marker)
//            } else {
//                dirtyTiles[path] = existing.toMutableSet().let {
//                    it.add(marker)
//                    it
//                }.toList()
//            }
//        }
//    }

    // gets called by applyOverridesToTile only if tile contains marker with same or newer updatedAt
//    private fun removeOverride(marker: MapMarker) {
//        marker.position.pointCoordinates().tiles().forEach { t ->
//            val path = t.path
//            val dirtyTileMarkers = dirtyTiles[path]
//            if (dirtyTileMarkers != null) {
//                val removed = dirtyTileMarkers - marker
//                if (removed.isEmpty()) {
//                    // tile override is no longer needed
//                    dirtyTiles.remove(path)
//                } else {
//                    dirtyTiles[path] = removed
//                }
//            }
//        }
//    }

//    private fun applyOverridesToTile(tile: MapMarkerTileSearchResultsHolder): MapMarkerTileSearchResultsHolder {
//        val overrides = dirtyTiles[tile.mapMarkerTileSearchResults.tilePath]?.associate { it.id to it }
//        return if (overrides.isNullOrEmpty()) {
//            tile
//        } else {
//            // only do work if the tile is dirty and needs markers replaced
//            val clustersWithOverrides = tile.mapMarkerTileSearchResults.clusters.map { original ->
//                original.copy(
//                    markers = original.markers?.map { ogMarker ->
//                        if (ogMarker is MapMarker) {
//                            val replacement = overrides[ogMarker.id]
//                            if (replacement == null) {
//                                ogMarker
//                            } else {
//                                if (replacement.updatedAt <= ogMarker.updatedAt) {
//                                    // replacement is no longer needed so get rid of it
//                                    removeOverride(replacement)
//                                    ogMarker
//                                } else {
//                                    replacement
//                                }
//                            }
//                        } else {
//                            ogMarker
//                        }
//                    },
//                )
//            }
//            tile.mapMarkerTileSearchResults.copy(
//                clusters = clustersWithOverrides,
//            ).let {
//                MapMarkerTileSearchResultsHolder(it, hasOverrides = true)
//            }
//        }
//    }

//    fun invalidate(tiles: List<Tile>) {
//        tiles.forEach { cache.remove(it.path) }
//    }

//    private fun invalidate(point: LatLon) {
//        invalidate(point.pointCoordinates().tiles())
//    }

    suspend fun fetchTiles(state: MapState, searchSpec: SearchSpec): List<MapMarkerTileSearchResultsHolder> {
        val (zl, requiredTiles) = if (state.zoom < 4) {
            3 to Tile.allTilesAt(3).toList()
        } else {
            state.copy(
                zoom = min(state.zoom, Tile.MAX_ZOOM.toDouble()),
            ).tiles()
        }

        val cacheHits = requiredTiles.mapNotNull { cache[it.path] }

        @Suppress("ConvertArgumentToSet") // not worth it; the list is small
        val remaining = requiredTiles - cacheHits.map { Tile.fromPath(it.mapMarkerTileSearchResults.tilePath) }

        return if (remaining.isEmpty()) {
            cacheHits
        } else {

            val (tileResults, time) = measureTimedValue {
                // reduce or increase as needed to keep a reasonable number of markers per tile
                val toFetch = when {
                    // at high zoom levels show everything
                    zl >= 22 -> 400
                    zl > 21 -> 200
                    zl > 20 -> 150
                    zl > 19 -> 125
                    zl > 18 -> 100
                    zl > 17 -> 75
                    zl > 16 -> 50
                    zl > 15 -> 20
                    zl > 14 -> 10
                    zl > 13 -> 5
                    // fetch at least 1 object to show it directly,
                    // in case it is the only object in this tile
                    else -> 1
                }
                logger.info {
                    "ClusterService: Zl: $zl: ${describeZoomLevel(zl)}.\nFetching ${remaining.size} tiles out of ${requiredTiles.size} with max $toFetch markers each. Cache size: ${cache.size}"
                }
                formationClient.getTiles(
                    zoom = zl,
                    tiles = remaining,
                    searchSpec = searchSpec,
                    size = 100, //toFetch,
                    from = 0,
                ).getOrElse { error ->
                    logger.warn(error) { "failed to fetch tiles; return empty list" }
                    emptyList()
                }
            }
            if (tileResults.isNotEmpty()) {
                logger.info { "fetched ${remaining.size} tiles in $time" }
            }
            tileResults.map { res ->
                // If we are in zl range below 14:
                // Check if there are more than 1 results in the tile
                val modifiedResults = when {
                    (zl < 13 && res.total > 1) -> {
                        // yes -> remove the fetched objects (should be only one) from results
                        // and adjust amount and colorBreakdown to show only cluster(s)
                        val cleanedUpClusters = res.clusters.map { cluster ->
                            if (cluster.fetched > 0) {
                                val newColorBreakdown = cluster.markers?.filterIsInstance<MapMarker>()?.let { markers ->
                                    val mutable = cluster.colorBreakDown.toMutableMap()
                                    markers.map { marker ->
                                        // fallback to default color to cover objects without color
                                        (marker.color ?: resolveColor(MarkerColor.Default)).let { color ->
                                            mutable[color] = mutable.getOrPut(color) { 0 } + 1
                                        }
                                    }
                                    mutable
                                } ?: cluster.colorBreakDown
                                cluster.copy(
                                    amount = cluster.amount + cluster.fetched,
                                    colorBreakDown = newColorBreakdown,
                                    markers = null,
                                )
                            } else cluster
                        }
                        res.copy(clusters = cleanedUpClusters)
                    }

                    else -> {
                        // no -> keep the 1 fetched object to show it directly
                        res
                    }
                }

                val holder = MapMarkerTileSearchResultsHolder(modifiedResults)
                cache[modifiedResults.tilePath] = holder
                holder
            } + cacheHits
        }.also {
            if (remaining.size < it.size) {
                logger.info { "returning ${it.size - remaining.size} tiles from cache " }
            }
        }
    }
}

fun describeZoomLevel(zoom: Int): String {
    return when (zoom) {
        0 -> "Whole Earth view – the entire world fits into a single tile."
        1 -> "Continent view – you can see all continents and major geographic features."
        2 -> "Country-level view – countries are distinguishable, but no city details."
        3 -> "Large country view – countries are clearly visible, and major cities are recognizable."
        4 -> "Regional view – large regions within countries become clear, major metropolitan areas visible."
        5 -> "State/Province view – states or provinces are clearly defined, large cities are prominent."
        6 -> "Large city view – metropolitan areas take shape, but individual buildings are not visible."
        7 -> "City view – a full city can be seen, neighborhoods and major roads are distinguishable."
        8 -> "City and surroundings – suburbs appear, major roads and some parks are noticeable."
        9 -> "Urban region view – districts within a city are distinct, larger parks and rivers are clear."
        10 -> "Neighborhood overview – individual city districts are visible, highways and large roads stand out."
        11 -> "Detailed neighborhood view – local roads and some larger buildings are distinguishable."
        12 -> "District level – individual streets and large landmarks become visible."
        13 -> "Block level – detailed streets, parks, and larger buildings are clear."
        14 -> "Street level – all roads are visible, buildings and smaller landmarks become distinguishable."
        15 -> "Closer street level – most buildings are clearly visible, smaller roads and alleys appear."
        16 -> "Block-by-block view – individual houses and building details are clear."
        17 -> "Detailed house level – individual buildings, trees, and parking lots are distinguishable."
        18 -> "House and street view – individual cars and small details like benches are visible."
        19 -> "Backyard level – clear details of houses, small paths, and some vegetation."
        20 -> "Building level – individual windows, parked cars, and small objects are recognizable."
        21 -> "Close-up view – details like backyard furniture, small roads, and even people are visible."
        22 -> "Extreme close-up – the highest level of detail, suitable for security cameras and high-resolution maps."
        else -> "Unknown zoom level – outside the usual range (0-22)."
    }
}
